import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocListItem,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";
import {
  leadInItem,
  paragraphsOrPrompt,
  parseWholeCount,
  splitItems,
  splitLeadIn,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A research statement outline for a faculty or research-track application: an overview,
 * past work, current work, future plans, impact, funding and people, each with a word
 * target from the total, the writer's own projects placed in them, and prompts for what
 * is missing.
 *
 * The split is a planning aid. A research university reads for a fundable programme and
 * puts the weight on the future; a teaching-focused institution reads for a programme
 * that undergraduates can join and weights people and mentoring more. Length varies by
 * field — roughly one to two pages in the humanities, two to three in the social
 * sciences, three to four in the laboratory sciences — and the advertisement's own
 * limit always wins.
 */

export const RESEARCH_INSTITUTIONS = ["research", "teaching"] as const;
export type ResearchInstitution = (typeof RESEARCH_INSTITUTIONS)[number];

export const RESEARCH_SECTIONS = [
  "overview",
  "past",
  "current",
  "future",
  "impact",
  "funding",
  "people",
] as const;
export type ResearchSection = (typeof RESEARCH_SECTIONS)[number];

export const RESEARCH_SHARES: Record<
  ResearchInstitution,
  Record<ResearchSection, number>
> = {
  research: {
    overview: 8,
    past: 15,
    current: 20,
    future: 32,
    impact: 8,
    funding: 10,
    people: 7,
  },
  teaching: {
    overview: 8,
    past: 15,
    current: 20,
    future: 25,
    impact: 8,
    funding: 6,
    people: 18,
  },
};

export const WORDS_PER_PAGE = 500;
export const PAGE_CHOICES = ["pages2", "pages3", "pages4", "custom"] as const;
export type PageChoice = (typeof PAGE_CHOICES)[number];
export const CHOICE_WORDS: Record<Exclude<PageChoice, "custom">, number> = {
  pages2: 1000,
  pages3: 1500,
  pages4: 2000,
};
export const MIN_WORDS = 300;
export const MAX_WORDS = 4000;

export interface ResearchStatementInput {
  institution: ResearchInstitution;
  name: string;
  /** The length in words, as typed; used when the length choice is custom. */
  words: string;
  overview: string;
  /** One project per line: "Project — what you found". */
  past: string;
  /** One project per line: "Project — where it stands". */
  current: string;
  /** One project per line: "Project — the first step". */
  future: string;
  impact: string;
  /** One line each: "Funder or scheme — what it would support". */
  funding: string;
  /** One line each: collaborators, students, partners. */
  people: string;
}

export interface ResearchStatementLabels {
  defaultTitle: string;
  subtitles: Record<ResearchInstitution, string>;
  subtitleWords: string;
  budgetHeading: string;
  budgetNote: string;
  budget: { section: string; share: string; words: string; total: string };
  sections: Record<ResearchSection, string>;
  target: string;
  targetShare: string;
  tips: Record<ResearchInstitution, Record<ResearchSection, string>>;
  prompts: Record<ResearchSection, string>;
  futureHead: { project: string; step: string };
  fundingHead: { funder: string; supports: string };
  checklistHeading: string;
  checklist: readonly string[];
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const PERCENT: Intl.NumberFormatOptions = { style: "percent" };

/** The target in words, or null when it is missing or outside a sensible range. */
export function researchWordTotal(
  choice: PageChoice,
  rawWords: string
): number | null {
  if (choice !== "custom") {
    return CHOICE_WORDS[choice];
  }
  const typed = parseWholeCount(rawWords);
  return typed !== null && typed >= MIN_WORDS && typed <= MAX_WORDS
    ? typed
    : null;
}

/** Word targets per section, in fives, adding up to the total. */
export function researchTargets(
  total: number,
  institution: ResearchInstitution
): Record<ResearchSection, number> {
  const words = allocateWords(
    total,
    RESEARCH_SECTIONS.map((section) => RESEARCH_SHARES[institution][section]),
    5
  );
  const targets = {} as Record<ResearchSection, number>;
  RESEARCH_SECTIONS.forEach((section, index) => {
    targets[section] = words[index];
  });
  return targets;
}

/** "Project — detail" → [project, detail]; a line with no separator has no detail. */
export function parseProjects(text: string): [string, string][] {
  return splitItems(text).map((line) => splitLeadIn(line) ?? [line, ""]);
}

function projectList(text: string, prompt: string): DocBlock[] {
  const lines = splitItems(text);
  if (lines.length === 0) {
    return [{ kind: "prompt", text: prompt }];
  }
  const items: DocListItem[] = lines.map((line) => ({
    text: leadInItem(line),
  }));
  return [{ kind: "list", list: { style: "bullet", items } }];
}

function tableOrPrompt(
  text: string,
  head: readonly [string, string],
  prompt: string
): DocBlock[] {
  const rows = parseProjects(text);
  if (rows.length === 0) {
    return [{ kind: "prompt", text: prompt }];
  }
  return [{ kind: "table", head, rows }];
}

function body(
  section: ResearchSection,
  input: ResearchStatementInput,
  labels: ResearchStatementLabels
): DocBlock[] {
  const prompt = labels.prompts[section];
  switch (section) {
    case "overview":
      return paragraphsOrPrompt(input.overview, prompt);
    case "past":
      return projectList(input.past, prompt);
    case "current":
      return projectList(input.current, prompt);
    case "future":
      return tableOrPrompt(
        input.future,
        [labels.futureHead.project, labels.futureHead.step],
        prompt
      );
    case "impact":
      return paragraphsOrPrompt(input.impact, prompt);
    case "funding":
      return tableOrPrompt(
        input.funding,
        [labels.fundingHead.funder, labels.fundingHead.supports],
        prompt
      );
    default:
      return projectList(input.people, prompt);
  }
}

function budgetBlock(
  labels: ResearchStatementLabels,
  institution: ResearchInstitution,
  targets: Record<ResearchSection, number> | null,
  fmt: Format
): DocBlock {
  const rows = RESEARCH_SECTIONS.map((section) => {
    const row = [
      labels.sections[section],
      fmt(RESEARCH_SHARES[institution][section] / 100, PERCENT),
    ];
    return targets ? [...row, fmt(targets[section])] : row;
  });
  const sum = targets
    ? Object.values(targets).reduce((total, value) => total + value, 0)
    : 0;
  const totalRow = [labels.budget.total, fmt(1, PERCENT)];
  const head = [labels.budget.section, labels.budget.share];
  return {
    kind: "table",
    head: targets ? [...head, labels.budget.words] : head,
    rows: [...rows, targets ? [...totalRow, fmt(sum)] : totalRow],
  };
}

export function buildResearchStatement(
  input: ResearchStatementInput,
  labels: ResearchStatementLabels,
  choice: PageChoice,
  fmt: Format
): DocumentModel {
  const total = researchWordTotal(choice, input.words);
  const targets = total ? researchTargets(total, input.institution) : null;
  const sections: DocSection[] = RESEARCH_SECTIONS.map((section) => {
    const share = fmt(
      RESEARCH_SHARES[input.institution][section] / 100,
      PERCENT
    );
    const target = targets
      ? t(labels.target, { words: fmt(targets[section]), share })
      : t(labels.targetShare, { share });
    return {
      heading: labels.sections[section],
      blocks: [
        {
          kind: "guidance",
          text: `${target} ${labels.tips[input.institution][section]}`,
        },
        ...body(section, input, labels),
      ],
    };
  });
  const subtitle = labels.subtitles[input.institution];
  return {
    title: input.name.trim() || labels.defaultTitle,
    subtitle: total
      ? t(labels.subtitleWords, { kind: subtitle, words: fmt(total) })
      : subtitle,
    sections: [
      {
        heading: labels.budgetHeading,
        blocks: [
          { kind: "guidance", text: labels.budgetNote },
          budgetBlock(labels, input.institution, targets, fmt),
        ],
      },
      ...sections,
      {
        heading: labels.checklistHeading,
        blocks: [
          {
            kind: "checklist",
            items: labels.checklist.map((text) => ({ text })),
          },
        ],
      },
    ],
  };
}
